/**
 * 드롭다운 메뉴의 클래스 문자열 단일 정의.
 *
 * DropDown.tsx 와 인하우스 Select(../forms/selects/Select.tsx)가 완전히 같은 메뉴/옵션
 * 룩을 공유한다. 예전에는 emotion 의 dropdowns/styles/*.ts 를 react-select 가 재사용하는
 * 구조였는데, react-select 를 걷어내면서 두 곳이 갈라지지 않도록 여기 한 곳에만 둔다.
 *
 * tailwind.css 가 `@import 'tailwindcss' important` 라 모든 유틸리티가 !important 다.
 * 같은 속성을 건드리는 유틸리티를 두 개 붙이면 className 순서가 아니라 CSS 출력 순서가
 * 승자를 정하므로, 상태별로 갈리는 색/배경은 JS 에서 하나만 골라 emit 한다.
 * (base 와 hover: 처럼 변형 단계가 다른 경우는 specificity 로 hover 가 이기므로 안전하다.)
 *
 * 메뉴 박스에는 위치/여백을 넣지 않는다. 호출부마다 위/아래 배치가 달라
 * margin-top / margin-bottom 이 충돌하기 때문에 배치는 각 호출부의 래퍼가 담당한다.
 */

export type DropDownItemState = {
  /** 선택된 값 */
  isActive?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  /** 키보드/마우스로 하이라이트된 항목 (aria-activedescendant 대상) */
  isFocused?: boolean;
};

/** menuList 를 감싸는 흰 박스. 폭은 호출부에서 style 로 지정한다. */
export function dropDownMenuClass(isSmallSize: boolean) {
  return [
    'z-[1] rounded-xl bg-apps-coreGrayWhite shadow-[0_12px_32px_rgba(17,24,39,0.16),0_2px_8px_rgba(17,24,39,0.08)]',
    isSmallSize ? 'w-max max-w-[160px] p-1' : 'p-2',
  ].join(' ');
}

/**
 * 옵션 리스트(스크롤 컨테이너).
 * relative 는 옵션의 offsetTop 을 리스트 기준으로 읽어 스크롤을 계산하기 위한 것이다.
 */
export const dropDownListClass =
  'custom-scroll-dropdown relative max-h-[280px] overflow-x-hidden overflow-y-scroll [scrollbar-gutter:stable]';

/** 옵션 한 줄 */
export function dropDownItemClass(
  isSmallSize: boolean,
  item: DropDownItemState,
) {
  const color = item.isDisabled
    ? 'text-apps-coreProductDisableContent'
    : item.hasError
      ? 'text-apps-coreProductError01'
      : item.isActive
        ? 'text-apps-corePrimary01'
        : 'text-apps-coreGrayBlack';

  const background = item.isDisabled
    ? 'bg-apps-coreGrayWhite hover:bg-apps-coreGrayWhite'
    : item.isFocused
      ? 'bg-apps-coreGray07 hover:bg-apps-coreGray07'
      : 'bg-apps-coreGrayWhite hover:bg-apps-coreGray07';

  return [
    'flex items-center gap-2.5 self-stretch rounded px-4 py-2 font-normal leading-[140%] [text-overflow:clip] [word-break:break-all]',
    isSmallSize
      ? 'min-h-10 text-sm tracking-[-0.28px]'
      : 'min-h-12 text-base tracking-[-0.32px]',
    item.isDisabled ? 'cursor-default' : 'cursor-pointer',
    background,
    color,
  ].join(' ');
}
